/**
 * Where the corner text goes when the image leaves one side of the cell empty (a mammogram against
 * the chest wall, a sagittal MPR, an image panned aside): every corner block gathers on the empty
 * side instead of the right (or left) blocks sitting on the tissue. Decided from the image's real box
 * on screen, never from tags, so it follows pans, zoom, flips and rotation.
 */

export type LabelSide = 'left' | 'right';

export interface LabelLayout {
  cellWidth: number;
  /** Image box on screen, in the cell's CSS pixels. */
  imageLeft: number;
  imageRight: number;
  /** Widths of the widest left-corner and right-corner blocks. */
  leftBlock: number;
  rightBlock: number;
}

/** A side must leave at least this share of the cell free to take the labels. */
export const MIN_FREE_SHARE = 0.22;
/** Hysteresis: keep the current side unless the other one is freer by this share (no flip-flop while panning). */
export const SWITCH_MARGIN_SHARE = 0.1;

function gaps(layout: LabelLayout): { left: number; right: number } {
  const clamp = (v: number) => Math.min(layout.cellWidth, Math.max(0, v));
  return { left: clamp(layout.imageLeft), right: clamp(layout.cellWidth - layout.imageRight) };
}

/** The side all labels gather on, or null for the usual four corners. */
export function chooseLabelSide(layout: LabelLayout, previous: LabelSide | null): LabelSide | null {
  if (!(layout.cellWidth > 0)) return null;
  const gap = gaps(layout);
  const overlapsImage = layout.leftBlock > gap.left || layout.rightBlock > gap.right;
  if (!overlapsImage) return null;
  const minFree = MIN_FREE_SHARE * layout.cellWidth;
  const margin = SWITCH_MARGIN_SHARE * layout.cellWidth;
  if (previous) {
    const other: LabelSide = previous === 'left' ? 'right' : 'left';
    if (gap[previous] >= minFree - margin && gap[other] - gap[previous] <= margin) return previous;
  }
  const best: LabelSide = gap.left >= gap.right ? 'left' : 'right';
  return gap[best] >= minFree ? best : null;
}

/** Free width on a side, for the blocks' max-width while they sit there. */
export function freeWidth(layout: LabelLayout, side: LabelSide): number {
  return gaps(layout)[side];
}
